import React from 'react';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { ForecastModel } from '@app/common/static-data/models';
import { ForecastEChart } from './ForecastEChart';
import { WeatherSummaryView } from './WeatherSummaryView';
import { PlottableForecast, VariableId, ModelId } from '../types';
import { useModelVariableValidation } from '../hooks/useModelVariableValidation';
import { useModelMissingVariableToast } from '../hooks/useModelMissingVariableToast';

interface Location {
  lat: number;
  lon: number;
  name: string;
}

interface ForecastChartContainerProps {
  loading: boolean;
  forecastData: Record<ModelId, PlottableForecast>;
  selectedParam: VariableId;
  selectedModels: ForecastModel[];
  endHour: number;
  location: Location;
  customModelColors: Record<string, string>;
}

// Utility function to extract city name from full location name
const getCityName = (fullName: string): string => {
  // Split by comma and take the first part (city name)
  return fullName.split(',')[0].trim();
};

export function ForecastChartContainer({
  loading,
  forecastData,
  selectedParam,
  selectedModels,
  endHour,
  location,
  customModelColors,
}: ForecastChartContainerProps) {
  // Check if weather summary is selected
  const isWeatherSummary = selectedParam === 'weather_summary';

  // Use custom hooks for validation and toast notifications (skip for weather summary)
  const validation = useModelVariableValidation(
    forecastData,
    selectedModels,
    selectedParam,
    loading,
  );

  useModelMissingVariableToast({
    modelsWithoutData: validation.modelsWithoutData,
    selectedModels,
    selectedParam,
    hasAnyData: validation.hasAnyData,
    isLoading: validation.isLoading,
    forecastData,
  });

  if (validation.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 space-y-4">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600 dark:text-theme-dark-text-secondary">
          Fetching forecast data...
        </p>
      </div>
    );
  }

  if (validation.hasNoForecastData) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 h-full text-gray-500 dark:text-theme-dark-text-secondary">
        <p className="text-center px-4">Select favorites first to render charts</p>
      </div>
    );
  }

  // Check if selected models have data for the selected parameter (skip for weather summary)
  if (!isWeatherSummary && !validation.hasAnyData) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 h-full text-gray-500 dark:text-theme-dark-text-secondary">
        <p className="text-center px-4">
          No model of your selection predicts this variable, select another model.
        </p>
      </div>
    );
  }

  // Render weather summary if that parameter is selected
  if (isWeatherSummary) {
    return (
      <div className="flex-1 relative h-full">
        <WeatherSummaryView
          forecastData={forecastData}
          selectedModels={selectedModels}
          location={location}
          customModelColors={customModelColors}
          endHour={endHour}
        />
      </div>
    );
  }

  // Render normal chart view
  return (
    <div className="flex-1 relative h-full">
      {/* Location Heading - Absolutely positioned to not affect layout, hidden on mobile */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-10 hidden sm:block">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 bg-white dark:bg-theme-dark-bg px-4 py-2 rounded-lg">
          {getCityName(location.name)}
        </h2>
      </div>

      {/* Chart */}
      <ForecastEChart
        data={forecastData}
        selectedParam={selectedParam}
        selectedModels={selectedModels}
        endHour={endHour}
        customModelColors={customModelColors}
      />
    </div>
  );
}
